import type { ComponentProps } from 'react';
import { cva, cx, type VariantProps } from '@/cva.config';

export const statusPillVariants = cva({
  base: 'inline-flex w-fit items-center rounded-sm px-1.5 py-px font-mono text-2xs tracking-wide uppercase',
  compoundVariants: [
    {
      class: 'bg-border text-foreground-muted',
      kind: 'job',
      status: 'new',
    },
    {
      class: 'bg-border text-foreground',
      kind: 'job',
      status: 'scored',
    },
    {
      class: 'bg-accent-soft text-accent',
      kind: 'job',
      status: 'ready',
    },
    {
      class: 'bg-score-high/15 text-score-high',
      kind: 'job',
      status: 'approved',
    },
    {
      class: 'bg-score-high/15 text-score-high',
      kind: 'job',
      status: 'applied',
    },
    {
      class: 'bg-score-low/15 text-score-low',
      kind: 'job',
      status: 'rejected',
    },
    {
      class: 'bg-border text-subtle',
      kind: 'job',
      status: 'expired',
    },
    {
      class: 'bg-score-low/15 text-score-low',
      kind: 'job',
      status: 'score_failed',
    },
    {
      class: 'bg-score-low/15 text-score-low',
      kind: 'job',
      status: 'tailor_failed',
    },
    {
      class: 'bg-score-mid/15 text-score-mid',
      kind: 'job',
      status: 'pending',
    },
    {
      class: 'bg-score-high/15 text-score-high',
      kind: 'job',
      status: 'submitted',
    },
    {
      class: 'border-border text-foreground-muted',
      kind: 'app',
      status: 'new',
    },
    {
      class: 'border-border text-foreground',
      kind: 'app',
      status: 'scored',
    },
    {
      class: 'border-accent text-accent',
      kind: 'app',
      status: 'ready',
    },
    {
      class: 'border-score-high text-score-high',
      kind: 'app',
      status: 'approved',
    },
    {
      class: 'border-score-high text-score-high',
      kind: 'app',
      status: 'applied',
    },
    {
      class: 'border-score-low text-score-low',
      kind: 'app',
      status: 'rejected',
    },
    {
      class: 'border-border text-subtle',
      kind: 'app',
      status: 'expired',
    },
    {
      class: 'border-score-low text-score-low',
      kind: 'app',
      status: 'score_failed',
    },
    {
      class: 'border-score-low text-score-low',
      kind: 'app',
      status: 'tailor_failed',
    },
    {
      class: 'border-score-mid text-score-mid',
      kind: 'app',
      status: 'pending',
    },
    {
      class: 'border-score-high text-score-high',
      kind: 'app',
      status: 'submitted',
    },
  ],
  variants: {
    kind: {
      app: 'gap-1 border border-dashed bg-transparent',
      job: '',
    },
    status: {
      applied: '',
      approved: '',
      expired: '',
      new: '',
      pending: '',
      ready: '',
      rejected: '',
      score_failed: '',
      scored: '',
      submitted: '',
      tailor_failed: '',
    },
  },
});

export type StatusPillStatus = NonNullable<
  ComponentProps<typeof StatusPill>['status']
>;

const STATUS_LABELS: Record<StatusPillStatus, string> = {
  applied: 'applied',
  approved: 'approved',
  expired: 'expired',
  new: 'new',
  pending: 'pending',
  ready: 'ready',
  rejected: 'rejected',
  score_failed: 'score failed',
  scored: 'scored',
  submitted: 'submitted',
  tailor_failed: 'tailor failed',
};

interface StatusPillProps extends VariantProps<typeof statusPillVariants> {
  className?: string;
}

export const StatusPill = ({
  className,
  kind = 'job',
  status = 'new',
}: StatusPillProps) => (
  <span className={cx(statusPillVariants({ kind, status }), className)}>
    {kind === 'app' && <span className="opacity-60">app:</span>}
    {STATUS_LABELS[status]}
  </span>
);
